import { AlertCircle, Pencil, RotateCcw, Trash2 } from "lucide-react";
import type { OutboxQueueItem } from "@/lib/outbox/outboxQueue";

type Props = {
  item: OutboxQueueItem;
  accountEmail: string;
  onRetry: () => void;
  onEdit: () => void;
  onDismiss: () => void;
};

const STATUS_LABELS: Record<OutboxQueueItem["status"], string> = {
  pending: "En attente d'envoi…",
  sending: "Envoi en cours…",
  failed: "Échec de l'envoi",
};

export function OutboxMessageReader({
  item,
  accountEmail,
  onRetry,
  onEdit,
  onDismiss,
}: Props) {
  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col">
      {item.status === "failed" ? (
        <div className="flex shrink-0 items-center justify-end gap-1 border-b border-[var(--mail-border-soft)] px-3 py-2">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-[var(--mail-accent)] hover:bg-[var(--mail-accent-soft)]"
          >
            <Pencil className="h-3.5 w-3.5" />
            Modifier
          </button>
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-[var(--mail-accent)] hover:bg-[var(--mail-accent-soft)]"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Réessayer
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)]"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Retirer
          </button>
        </div>
      ) : null}

      <div className="shrink-0 space-y-2 border-b border-[var(--mail-border-soft)] px-5 py-4">
        <div
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${
            item.status === "failed"
              ? "bg-red-500/10 text-red-600"
              : "bg-[var(--mail-accent-soft)] text-[var(--mail-accent)]"
          }`}
        >
          {item.status === "failed" ? (
            <AlertCircle className="h-3.5 w-3.5" />
          ) : null}
          {STATUS_LABELS[item.status]}
        </div>

        <h2 className="text-lg font-semibold leading-snug">
          {item.subject.trim() || "(Sans objet)"}
        </h2>

        <p className="text-sm text-[var(--mail-muted)]">
          De <span className="text-[var(--mail-text)]">{accountEmail}</span>
        </p>
        <p className="text-sm text-[var(--mail-muted)]">
          À <span className="text-[var(--mail-text)]">{item.to}</span>
        </p>
        {item.cc?.trim() ? (
          <p className="text-sm text-[var(--mail-muted)]">
            Cc <span className="text-[var(--mail-text)]">{item.cc}</span>
          </p>
        ) : null}

        {item.error ? (
          <p className="mail-alert-error px-3 py-2 text-sm" role="alert">
            {item.error}
          </p>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap px-5 py-4 text-sm leading-relaxed text-[var(--mail-text)]">
        {item.bodyText}
      </div>
    </div>
  );
}
